import { TextInput, Textarea, Select, Stack, SimpleGrid } from "@mantine/core";
import { UserSelect } from "../../components/UserSelect.js";
import { useForm } from "@mantine/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { type Asset, AssetType, Classification } from "@iso27001/shared";
import { assetsApi } from "../../api/assets.js";
import { ApiError } from "../../api/client.js";
import { useEnumLabel } from "../../i18n/enum-label.js";
import { useApiErrorMessage } from "../../i18n/api-error.js";
import { EntityModal } from "../../components/EntityModal.js";
import { useRecordAccess } from "../../components/use-record-access.js";

interface Props {
  opened: boolean;
  onClose: () => void;
  initial: Asset | null;
}

export function AssetFormModal({ opened, onClose, initial }: Props) {
  const qc = useQueryClient();
  const isEdit = !!initial;
  const { t } = useTranslation();
  const { label } = useEnumLabel();
  const apiError = useApiErrorMessage();
  // Assets have no closed state; only viewers see them read-only.
  const { readOnly } = useRecordAccess(false);

  const typeOptions = AssetType.options.map((value) => ({ value, label: label("assetType", value) }));
  const classificationOptions = Classification.options.map((value) => ({ value, label: label("clearance", value) }));

  const form = useForm({
    initialValues: {
      code: initial?.code ?? "",
      name: initial?.name ?? "",
      type: initial?.type ?? "",
      description: initial?.description ?? "",
      classification: initial?.classification ?? "",
      assetOwnerUserId: initial?.assetOwnerUserId ?? null as number | null,
      riskOwnerUserId: initial?.riskOwnerUserId ?? null as number | null,
    },
  });

  const mut = useMutation({
    mutationFn: async (vals: typeof form.values) => {
      const payload: Record<string, unknown> = {
        name: vals.name,
        type: vals.type as Asset["type"],
        description: vals.description || null,
        classification: (vals.classification || null) as Asset["classification"],
        assetOwnerUserId: vals.assetOwnerUserId ?? null,
        riskOwnerUserId: vals.riskOwnerUserId ?? null,
      };
      // Include code only when editing or when user explicitly provided one on create
      if (isEdit || vals.code.trim() !== "") {
        payload.code = vals.code;
      }
      return isEdit
        ? assetsApi.update(initial!.id, payload as Parameters<typeof assetsApi.update>[1])
        : assetsApi.create(payload as Parameters<typeof assetsApi.create>[0]);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assets"] });
      notifications.show({ message: isEdit ? t("assets.form.notify.updated") : t("assets.form.notify.created"), color: "teal" });
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "Conflict") {
        form.setFieldError("code", t("assets.error.codeConflict"));
      } else {
        notifications.show({ message: apiError(e, { fallback: "assets.error.saveFailed" }), color: "red" });
      }
    },
  });

  return (
    <EntityModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t("assets.form.title") : t("assets.newAsset")}
      code={initial?.code}
      readOnly={readOnly}
      onSubmit={form.onSubmit((vals) => mut.mutate(vals))}
      saving={mut.isPending}
      submitLabel={isEdit ? t("common.action.save") : t("common.action.createSubmit")}
    >
      <Stack>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {!readOnly && (
            <TextInput
              label={isEdit ? t("common.field.code") : t("common.field.codeOptional")}
              description={isEdit ? undefined : t("assets.form.code.autoHint")}
              // Hint under the input, so the field lines up with its neighbours in the row.
              inputWrapperOrder={["label", "input", "description", "error"]}
              required={isEdit}
              maxLength={80}
              placeholder={isEdit ? undefined : t("common.field.codeAutoPlaceholder")}
              {...form.getInputProps("code")}
            />
          )}
          <Select
            label={t("assets.field.type")}
            required
            readOnly={readOnly}
            data={typeOptions}
            {...form.getInputProps("type")}
          />
          {readOnly && (
            <Select
              label={t("assets.field.classification")}
              data={classificationOptions}
              readOnly
              {...form.getInputProps("classification")}
            />
          )}
        </SimpleGrid>
        <TextInput label={t("common.field.name")} required readOnly={readOnly} maxLength={500} {...form.getInputProps("name")} />
        <Textarea label={t("common.field.description")} readOnly={readOnly} autosize minRows={readOnly ? 1 : 2} maxLength={5000} {...form.getInputProps("description")} />
        {!readOnly && (
          <Select
            label={t("assets.field.classification")}
            data={classificationOptions}
            clearable
            {...form.getInputProps("classification")}
          />
        )}
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <UserSelect
            label={t("assets.form.assetOwner.label")}
            placeholder={t("common.field.userPlaceholder")}
            readOnly={readOnly}
            clearable={!readOnly}
            value={form.values.assetOwnerUserId}
            onChange={(val) => form.setFieldValue("assetOwnerUserId", val)}
            error={form.errors.assetOwnerUserId}
          />
          <UserSelect
            label={t("assets.form.riskOwner.label")}
            placeholder={t("common.field.userPlaceholder")}
            readOnly={readOnly}
            clearable={!readOnly}
            value={form.values.riskOwnerUserId}
            onChange={(val) => form.setFieldValue("riskOwnerUserId", val)}
            error={form.errors.riskOwnerUserId}
          />
        </SimpleGrid>
      </Stack>
    </EntityModal>
  );
}
